import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import {
    reverseSessionSubdomain,
    getSubdomainFromSession,
    getCustomization,
} from "components/helper/callApi";
import { useSubdomain } from "components/helper/SubdomainContext";

import {
    Customization,
    saveCustomization,
    Subdomain,
} from "services/customizationService";
import { getSubdomain } from "services/urlService";

import { ERROR } from "types/snackBar.constant";
import { LS_FOOTPRINT_KEY } from "constants/constants";

const SubdomainHandler = ({ children }: any) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setSubdomainHandlingCompleted } = useSubdomain();

    const subdomain = getSubdomain();
    const currentPath = window.location.pathname;

    const handleSubdomainCodeOnline = async () => {
        try {
            const codeOnline: string | null = await reverseSessionSubdomain();
            if (!codeOnline) return;
            navigate(`/login/${codeOnline}`);
        } catch (error) {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
            return;
        }
    };

    const redirectToSubdomain = async (codeOnline: string) => {
        try {
            if (!codeOnline) {
                return;
            }

            const data: Subdomain | null =
                await getSubdomainFromSession(codeOnline);
            if (!data) return;
            window.location.replace(
                `https://${data.subdomain}.${data.url.replace("https://", "")}/login/${data.codeOnline}`,
            );
            return;
        } catch (error) {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
            return;
        }
    };

    const handleCustomization = async (customization: Customization) => {
        try {
            const existingFootprint = localStorage.getItem(LS_FOOTPRINT_KEY);

            if (customization.footprint === existingFootprint) {
                // Entity didn't changed, no need to save it
                return;
            }

            saveCustomization(customization);
        } catch (error) {
            enqueueSnackbar(`${t("errorUnknown")}`, {
                variant: ERROR,
            });
        }
    };

    useEffect(() => {
        const init = async () => {
            setSubdomainHandlingCompleted(false);

            try {
                if (subdomain) {
                    const customization: Customization | null =
                        await getCustomization();
                    const baseUrl = window.env
                        .REACT_APP_ONLINE_API_BASE_URL as string;
                    if (!customization && !baseUrl.includes("local")) {
                        window.location.replace(`${baseUrl}${currentPath}`);
                        return;
                    }

                    if (currentPath === "/") {
                        await handleSubdomainCodeOnline();
                    }

                    if (customization) {
                        await handleCustomization(customization);
                    }
                } else {
                    if (currentPath.includes(`/login/`)) {
                        await redirectToSubdomain(
                            currentPath.split("/login/")[1],
                        );
                    }
                }
            } catch (error) {
                enqueueSnackbar(`${t("errorUnknown")}`, {
                    variant: ERROR,
                });
            } finally {
                setSubdomainHandlingCompleted(true);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subdomain, currentPath]);

    return children;
};

export default SubdomainHandler;
